import { INVALID_DATE } from "../../../common/constants/ApiConstants";
import {
    convertStringToDate,
    isDateNotSqlOverflow,
    isAuthDateRequired,
    displayedServiceDay,
} from "./validations/localValidations";

/**
 * Classic's Details-tab date rules, as pure per-field functions Quick Entry can call (AB#35150).
 *
 * THE GAP. The shared date components (AuthorizationDateField, RequestDateField, ReceivedDateField,
 * DueDateField) render their error from a redux-form `meta`, and Quick Entry's call sites pass none -
 * it drives its own red/amber/green from FieldGroupRowStatus instead. So in Quick Entry these fields
 * showed no validation at all, and the row mappers key purely on "has a value", which meant a date
 * past the picker's maxDate came back green and confirmable. The pickers' maxDate stops a CLICK, not
 * a typed or AI-seeded value.
 *
 * Each function returns Classic's exact message, or "" when the value is acceptable, so the two panes
 * cannot word the same problem differently. Input is coerced through Classic's own
 * convertStringToDate because these values arrive as a Date (picker), an ISO string (rehydration) or
 * the AI's extracted string.
 *
 * TWO SEMANTICS WORTH NOT "TIDYING":
 *
 *  1. "Future" means >= TOMORROW midnight. Classic builds futureDate as today-at-midnight + 1 day and
 *     compares with >=, so TODAY always passes. Same-day requests are the common case; an off-by-one
 *     here would reject them.
 *  2. Due Date is NOT future-checked. Classic range-checks it and nothing else, because a due date is
 *     a deadline. The card asked for "no future dates on any date field" AND for parity with Classic;
 *     those conflict only here, and parity wins - see validateDueDate.
 *
 * Received Date checks SQL overflow BEFORE parseability - Classic's order for that field, and
 * still reproduced on purpose.
 *
 * Request Date's MISSING overflow check is no longer reproduced (AB#38460 follow-up, tester).
 * Classic omits it, so an out-of-range Request Date was the one of the six rows that stayed green
 * and confirmable: 01/01/1752 parses fine and is not in the future, so every rule this validator
 * had was satisfied. The other five all reject it and say so, which made Request Date look like a
 * bug rather than like parity. Parity with the SIBLING ROWS wins over parity with Classic here -
 * the specialist is looking at all six at once.
 *
 * The per-field WORDING quirks are not. Classic answered the same mistake three different ways -
 * Authorization folded overflow into "Invalid date.", Received gave the explicit range text,
 * Request had no overflow branch - and AB#38460 collapsed all of them to
 * OUT_OF_RANGE_DATE_MESSAGE, matching the two Service Date rows. A specialist who typed one bad
 * date should not get different explanations depending on which row they typed it into, and the
 * range text is the more useful of the two because it states what is allowed. Deliberate
 * divergence from Classic parity, requested on review - do not "restore" it.
 *
 * Never logs a value - these fields are PHI-adjacent (.claude/rules/hipaa-compliance.md).
 */

export type DateFieldValue = Date | string | undefined | null;

/**
 * Classic's out-of-range date message. Exported (AB#38460) so DataEntryDetails can reuse the
 * exact string for a Service Date the picker could not read at all, instead of introducing a
 * second wording for what the specialist sees as the same mistake. This text is already repeated
 * verbatim in localValidations.ts; a shared constant is how the row, the field and Confirm All
 * stay in step, so use it rather than retyping the literal.
 *
 * AB#38460 (follow-up, tester): the CODE now matches this SENTENCE, which it previously did not.
 * isDateNotSqlOverflow used to be strictly-less/greater, so 1/1/1753 and 12/31/9999 were ACCEPTED
 * while this text called them invalid - typing 01/01/1753 produced no error anywhere. An earlier
 * attempt corrected the MESSAGE instead and was reverted on review, because the same sentence is
 * hardcoded at four more sites in localValidations.ts (:105, :124, :454, :480).
 *
 * Fixed at the BOUND instead, in the shared predicate, so all five copies of this sentence became
 * true at once and there is still exactly one bound in the codebase. That does change Classic Mode
 * as well - deliberately, at the dev's request: Classic's Details tab is where the defect was
 * reported, and Classic's own Due Date check was a sixth inline copy of the bound that has been
 * routed through the predicate too.
 */
export const OUT_OF_RANGE_DATE_MESSAGE = "Date must be between 1/2/1753 and 12/30/9999";


/** Classic's futureDate: today at midnight plus one day. Anything >= this is a future date. */
function startOfTomorrow(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 1);
    return d;
}

function isEmpty(value: DateFieldValue): boolean {
    return value === undefined || value === null || value === "";
}

/** Parsed date, or undefined when the value is empty or cannot be read as a date at all. */
function parsed(value: DateFieldValue): Date | undefined {
    const date = convertStringToDate(value);
    if (!date || date === INVALID_DATE || typeof date !== "object") {
        return undefined;
    }
    return date as Date;
}

function isUnparseable(value: DateFieldValue): boolean {
    const date = parsed(value);
    return date === undefined || isNaN(date.getTime());
}

function isFuture(date: Date): boolean {
    return date.getTime() >= startOfTomorrow().getTime();
}

/**
 * Authorization Date. Required only for the request purposes Classic's isAuthDateRequired lists, and
 * only validated for content when a value is actually present - an empty optional field is fine.
 */
export function validateAuthorizationDate(value: DateFieldValue, requestPurposeId?: number): string {
    if (isEmpty(value)) {
        return isAuthDateRequired(requestPurposeId) ? "Authorization date is required." : "";
    }
    // AB#38460: reports the range message, not Classic's "Invalid date." for this field. Classic
    // words the same mistake three ways across these validators (this one folded overflow into
    // "Invalid date.", Received Date gave the range text, Request Date had no overflow check at
    // all); the specialist sees one mistake and should get one answer, and the range text is the
    // more useful of the two because it says what is actually allowed. This is a deliberate
    // divergence from Classic parity, requested on review.
    if (isUnparseable(value) || !isDateNotSqlOverflow(parsed(value) as Date)) {
        return OUT_OF_RANGE_DATE_MESSAGE;
    }
    return isFuture(parsed(value) as Date) ? "Authorization date must not be a future date." : "";
}

/** Request Date. Always required. */
export function validateRequestDate(value: DateFieldValue): string {
    if (isEmpty(value)) {
        return "Request date is required.";
    }
    // AB#38460: range message rather than "Invalid date." - see validateAuthorizationDate.
    //
    // The overflow predicate is the SAME one the other four use, not a hand-written bound, so all
    // six rows accept and reject exactly the same set. Its floor is strictly-less-than 1/1/1753,
    // one day off the message's own wording - see OUT_OF_RANGE_DATE_MESSAGE, where that off-by-one
    // is documented and knowingly retained across all five copies of the sentence.
    if (isUnparseable(value) || !isDateNotSqlOverflow(parsed(value) as Date)) {
        return OUT_OF_RANGE_DATE_MESSAGE;
    }
    return isFuture(parsed(value) as Date) ? "Request date must not be a future date." : "";
}

/**
 * Received Date. Required-ness is the caller's call (Classic derives it from the request type and the
 * facility's enableNonHIMReceivedDateRequired flag). Carries one rule no other date has: a floor of
 * 365 days in the past.
 */
export function validateReceivedDate(value: DateFieldValue, required: boolean): string {
    if (isEmpty(value)) {
        return required ? "Received date is required." : "";
    }
    // Overflow BEFORE parseability - Classic's order for this field. The ordering no longer
    // changes the text (AB#38460 made both answers the range message), but it is kept because a
    // future rule could reintroduce a difference, and this is the order Classic evaluates in.
    const date = parsed(value);
    if (date && !isDateNotSqlOverflow(date)) {
        return OUT_OF_RANGE_DATE_MESSAGE;
    }
    if (isUnparseable(value)) {
        return OUT_OF_RANGE_DATE_MESSAGE;
    }
    if (isFuture(date as Date)) {
        return "Received date must not be a future date.";
    }
    const floor = new Date().setHours(0, 0, 0, 0) - (365 * 24 * 60 * 60 * 1000);
    return (date as Date).getTime() < floor
        ? "Received date must not be prior to 365 days in the past."
        : "";
}

/**
 * Service Date From / To (AB#35150 follow-up).
 *
 * These already had future rules in Classic's infoToReleaseValidiation, which is why the first pass
 * skipped them - wrongly. Two things stopped those rules from biting in Quick Entry:
 *
 *  1. buildReleaseInfoWithDateRange writes `dateOfService` only when BOTH from and to are set and
 *     All Dates is off. infoToReleaseValidiation reads the range back off
 *     `releaseInfo.find(info => info.dateOfService)`, so with only a FROM picked - or before any
 *     records have loaded - it finds nothing and no date rule runs at all.
 *  2. The row mappers set `status: hasValue ? Confirmed : NeedsEntry` with no validity check, so even
 *     when the message DID render, the row stayed green and confirmable.
 *
 * Validating each date on its own, straight from the scratch range, sidesteps both.
 *
 * ORDERING IS NO LONGER SOLELY infoToReleaseValidiation's (AB#38463). It now also runs row-level,
 * as validateServiceDateRange below - this function stays per-field because ordering is the one
 * rule needing both ends at once. The DATE-OF-BIRTH FLOOR is still deliberately not duplicated
 * here and remains Confirm-All-only, which is why that path can still fail a save the rows did
 * not flag.
 *
 * Classic compares against `new Date()` (now) for these two rather than tomorrow-midnight. Kept
 * as-is: a date-only value picked as today is midnight, which is < now, so today still passes.
 */
export function validateServiceDate(value: DateFieldValue, which: "FROM" | "TO"): string {
    // Empty is legal mid-entry - All Dates covers the range, and a half-filled range is a normal
    // intermediate state while the specialist is still picking.
    if (isEmpty(value)) {
        return "";
    }
    // AB#38460: same range message as the overflow branch just below, and as the other five rows.
    // Reachable for an AI-seeded or rehydrated bad value; typed input is caught earlier by the
    // invalid flag in getQuickEntryDateErrors, which returns this same constant.
    if (isUnparseable(value)) {
        return OUT_OF_RANGE_DATE_MESSAGE;
    }
    const date = parsed(value) as Date;
    if (!isDateNotSqlOverflow(date)) {
        return OUT_OF_RANGE_DATE_MESSAGE;
    }
    return date > new Date() ? `${which} date should not be greater than the current date.` : "";
}

/**
 * Service Date From>To ordering (AB#38463). Kept SEPARATE from validateServiceDate above -
 * whose own doc comment correctly says ordering isn't duplicated there - because ordering is
 * the one Service Date rule that needs BOTH ends at once, unlike every per-field check in this
 * file. Only runs when neither end already has its own message, so a future-date or overflow
 * error still wins (matching infoToReleaseValidiation's if/else-if precedence). Messages and the
 * calendar-day comparison (AB#37995 - two ends can carry different midnight conventions) are
 * copied verbatim from infoToReleaseValidiation so the row-level check and Confirm All can never
 * disagree about wording.
 */
export function validateServiceDateRange(from: DateFieldValue, to: DateFieldValue): { from: string; to: string } {
    const none = { from: "", to: "" };
    if (isEmpty(from) || isEmpty(to) || isUnparseable(from) || isUnparseable(to)) {
        return none;
    }
    const fromDate = parsed(from) as Date;
    const toDate = parsed(to) as Date;
    if (displayedServiceDay(fromDate) <= displayedServiceDay(toDate)) {
        return none;
    }
    return {
        from: "FROM date should not be greater than TO date.",
        to: "TO date should be greater than FROM date.",
    };
}

/**
 * Due Date. Range only - NO future check, deliberately. A due date is a deadline, so a future value
 * is the normal case; Classic allows it and Quick Entry must not be stricter.
 */
export function validateDueDate(value: DateFieldValue): string {
    if (isEmpty(value)) {
        return "";
    }
    const date = parsed(value);
    // AB#38460: range message rather than "Invalid date." - see validateAuthorizationDate.
    if (!date || isNaN(date.getTime()) || !isDateNotSqlOverflow(date)) {
        return OUT_OF_RANGE_DATE_MESSAGE;
    }
    return "";
}
